import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, PlacementArray } from '@ng-bootstrap/ng-bootstrap';
import {
  getTagEntityTypeEnum,
  ITagsEntity,
  TagEntityTypeEnum
} from '@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.constant';
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { TagsService } from '@shared/services/tags.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, tap } from 'rxjs';
import { Tag } from '@models/common/tag';
import { TagComponent } from '@shared/components/commons/tag/tag.component';
import { SelectTagsDropdownComponent } from './select-tags-dropdown/select-tags-dropdown.component';

@Component({
  selector: 'app-entity-profile-tags',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    TagComponent,
    SelectTagsDropdownComponent,
    HasRoleDirective
  ],
  templateUrl: './entity-profile-tags.component.html',
  styleUrl: './entity-profile-tags.component.scss',
})
export class EntityProfileTagsComponent implements OnInit {
  private toastr = inject(ToastrService);
  private tagsService = inject(TagsService);

  @Input() entityProfile!: ITagsEntity;
  @Input() disableControls = false;
  @Input() dropdownPlacement: PlacementArray = 'bottom-right';

  noTagsAssignedMessage = 'No tags assigned';
  systemTags: Tag[] = [];
  actualTagsSignal = signal<Tag[]>([]);
  form = new FormGroup({
    search: new FormControl<string | null>(null),
    actualTags: new FormArray<FormControl<boolean>>([]),
    suggestedTags: new FormArray<FormControl<boolean>>([]),
  });

  ngOnInit(): void {
    this.actualTagsSignal.set(this.entityProfile.tags);
    this.systemTags = 'systemTags' in this.entityProfile ? this.entityProfile.systemTags : [];
  }

  addNewTag(): void {
    const { modalRef, instance } = this.tagsService.openCreateEditTagModal();
    instance.isEdit = false;
    modalRef.result.then((newTag: Tag) => {
        this.createOrEditTag(newTag);
      },
      () => {
      });
  }

  assignTag({ tag, assign }: { tag: Tag, assign: boolean }): void {
    if (assign && !this.entityProfile.tags.find(t => t.tagId === tag.tagId)) {
      this.entityProfile.tags = [...this.entityProfile.tags, tag];
    } else {
      this.entityProfile.tags = this.entityProfile.tags.filter(t => t.tagId !== tag.tagId);
    }

    this.actualTagsSignal.set(this.entityProfile.tags);
    this.tagsService.assignTag(tag, this.entityId, assign, this.entityType)
      .subscribe({
        error: (error) => {
          console.warn('[EntityProfileTagsComponent] assignUnassignTag: ', error);
          this.toastr.error('Failed to load tags.');
          return of();
        }
      });
  }

  private createOrEditTag(tag: Tag): void {
    this.tagsService.createOrUpdateTag(tag, this.entityId, true, this.entityType)
      .pipe(
        tap((tagId) => {
          this.assignTag({ tag: new Tag({ ...tag, tagId }), assign: true });
        }),
        catchError((error) => {
          console.warn('[EntityProfileTagsComponent] createOrUpdateTag: ', error);
          this.toastr.error('Failed to load tags.');
          return of([]);
        })
      )
      .subscribe();
  }

  get entityId(): string {
    return (this.entityProfile?.['entityId'] || this.entityProfile['individualId']) as string;
  }

  get entityType(): TagEntityTypeEnum {
    return getTagEntityTypeEnum(this.entityProfile);
  }
}
